import { useEffect, useRef, useState } from "react";
import {
  LineChart,
  useXScale,
  useDrawingArea,
  MarkElement,
} from "@mui/x-charts";

import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import dayjs from "dayjs";
import {
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";

/**
 * CommonLineGraph - A reusable dual-line chart component for statistics visualization.
 * Renders one or two area line series with month grouping labels on the x-axis.
 * Supports dynamic y-axis scaling, amount formatting, and CSS variable theming.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number[]} props.graphDataFirst - Primary data series (required)
 * @param {number[]} [props.graphDataSecond] - Secondary data series (optional)
 * @param {string[]} props.xAxisLabels - Date/day labels for x-axis (e.g., ["2025-01-01", ...] or ["Sun", "Mon"])
 * @param {string} props.graphDataFirstColor - CSS variable for first series color (e.g., "var(--settled)")
 * @param {string} [props.graphDataSecondColor] - CSS variable for second series color
 * @param {boolean} [props.enableDynamicZeroColoring] - Whether to apply dynamic coloring to second series based on zero/non-zero values
 * @param {string} [props.zeroValueColor] - CSS variable for zero values in second series
 * @param {string} [props.nonZeroValueColor] - CSS variable for non-zero values in second series
 * @param {number[]} [props.defaultDataItemsForXaxis] - Custom x-axis indices (falls back to 0..n)
 * @param {string} [props.period] - Time period string
 * @param {boolean} [props.amountFormatRequiredForLegend=true] - Whether to format tooltip values as INR amounts
 * @param {string} [props.amountFormatForLegend] - Amount unit for legend/tooltip (e.g., "thousand", "lakh", "crore")
 * @returns {JSX.Element} Rendered LineChart with custom styling and month labels
 */
const CommonLineGraph = ({
  graphDataFirst,
  graphDataSecond,
  xAxisLabels, // e.g. ["2025-01-01", "2025-01-02", ..., "2025-02-01"] or ["Sun", "Mon"]
  graphDataFirstColor,
  graphDataSecondColor = "var(--text-primary)",
  enableDynamicZeroColoring = false,
  zeroValueColor = "var(--failed)",
  nonZeroValueColor = "var(--settled)",
  defaultDataItemsForXaxis,
  period,
  amountFormatRequiredForLegend = true,
  amountFormatForLegend,
}) => {
  const [highlightedItemIndex, setHighlightedItemIndex] = useState(null);

  let xAxisArray = xAxisLabels;

  //NOTE: this is fallback function to check wheather data (xAxisLabels) coming from backend is day or date format
  const isDateArray = (data) =>
    data &&
    data.length > GlobalConfiguration?.lengthCheckZero &&
    data.every((item) => /^\d{4}-\d{2}-\d{2}$/.test(item));

  // Abbreviation map for amount units in y-axis labels
  const abbrMap = {
    Lakhs: "L",
    Crores: "Cr",
    Millions: "M",
    Billions: "B",
    Thousands: "K",
  };

  const formattedGraphDataFirst = graphDataFirst
    ? graphDataFirst.map((value) => value)
    : [];
  const formattedGraphDataSecond = graphDataSecond
    ? graphDataSecond.map((value) => value)
    : [];

  // Whether a data point is currently hovered (highlighted)
  const isHovered =
    highlightedItemIndex !== null && highlightedItemIndex !== undefined;

  // Value of second series at hovered index (or 0 if no second data)
  const hoveredSecondVal = isHovered
    ? (formattedGraphDataSecond[highlightedItemIndex] ??
      GlobalConfiguration?.lengthCheckZero)
    : null;

  const isHoveredSecondZero =
    hoveredSecondVal === GlobalConfiguration?.lengthCheckZero;

  // Whether second series has any non-zero values (used for gradient coloring)
  const hasSecondDataNonZero =
    enableDynamicZeroColoring &&
    formattedGraphDataSecond.some(
      (val) => val > GlobalConfiguration?.lengthCheckZero,
    );

  // --- Dynamic min/max ---
  const allData = [...formattedGraphDataFirst, ...formattedGraphDataSecond];
  const dataMax = Math.max(...allData);

  // Minimum always 0; maximum adds 20% headroom, fallback to 1 if all zeros
  const yAxisMin = 0;
  const yAxisMax = dataMax > 0 ? dataMax * 1.2 : 1;

  // Determine abbreviation suffix (K, L, M, Cr, B) based on max value
  const yAxisUnit = getAbbreviation(yAxisMax);

  // --- CSS Variable Resolution ---
  // Extracts computed value from CSS custom property (removes "var()" wrapper)
  const resolveCssColor = (colorString) => {
    const varName = colorString.replace(/var\(|\)/g, "").trim();
    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    return resolved;
  };

  // Resolve color CSS variables to actual color values for MUI Charts
  const graphDataFirstColorResolved = resolveCssColor(graphDataFirstColor);
  const graphDataSecondColorResolved = resolveCssColor(graphDataSecondColor);

  const zeroColorResolved = resolveCssColor(zeroValueColor);
  const nonZeroColorResolved = resolveCssColor(nonZeroValueColor);

  /**
   * Determines the active color for the second series line/area
   * Uses dynamic zero coloring when enabled, otherwise uses static color
   */
  const secondSeriesActiveColor = enableDynamicZeroColoring
    ? isHovered
      ? isHoveredSecondZero
        ? zeroColorResolved
        : nonZeroColorResolved
      : hasSecondDataNonZero
        ? nonZeroColorResolved
        : zeroColorResolved
    : graphDataSecondColorResolved;

  // --- Month Grouping Logic ---
  // Groups consecutive dates by month for bottom-axis month labels
  const monthGroups = [];
  let currentMonth = null;

  xAxisLabels.forEach((date, index) => {
    const month = dayjs(date).format("MMM YYYY");
    if (!currentMonth || currentMonth.name !== month) {
      currentMonth = { name: month, start: index, end: index };
      monthGroups.push(currentMonth);
    } else {
      currentMonth.end = index;
    }
  });

  /**
   * Gets the color for the second series at a specific index.
   * When dynamic zero coloring is enabled, returns zero/non-zero color based on value.
   */
  const getSecondSeriesColorAt = (index) => {
    if (!enableDynamicZeroColoring) return graphDataSecondColorResolved;
    const val =
      formattedGraphDataSecond[index] ?? GlobalConfiguration?.lengthCheckZero;
    return val === GlobalConfiguration?.lengthCheckZero
      ? zeroColorResolved
      : nonZeroColorResolved;
  };

  /**
   * Returns { ringColor, dotColor } for a mark at the given index.
   * Handles overlapping points where both series have the same value.
   */
  const getMarkColors = (isSecond, index) => {
    const hasSecond = !!graphDataSecond;
    const firstVal = formattedGraphDataFirst[index];
    const secondVal = formattedGraphDataSecond[index];
    const valuesAreSame =
      hasSecond && firstVal !== undefined && firstVal === secondVal;

    const secondColor = getSecondSeriesColorAt(index);

    // Both series overlap here -> ring = first series, center = second series
    if (valuesAreSame) {
      return { ringColor: graphDataFirstColorResolved, dotColor: secondColor };
    }

    // Normal case: ring and dot share the series color
    const color = isSecond ? secondColor : graphDataFirstColorResolved;
    return { ringColor: color, dotColor: color };
  };

  /**
   * HoverIndexWatcher - Tracks mouse position over chart to determine highlighted index.
   * Uses MUI's useXScale and useDrawingArea hooks to map mouse X to data index.
   */
  const HoverIndexWatcher = ({ onChange, maxIndex }) => {
    const scale = useXScale();
    const { left, width } = useDrawingArea();
    const gRef = useRef(null);

    useEffect(() => {
      const svg = gRef.current?.ownerSVGElement;
      if (!svg || !scale?.invert) return;

      const handleMove = (e) => {
        const x = e.clientX - svg.getBoundingClientRect().left;
        if (x < left || x > left + width) {
          onChange(null);
          return;
        }
        const idx = Math.round(scale.invert(x));
        onChange(
          Math.min(
            Math.max(idx, GlobalConfiguration?.lengthCheckZero),
            maxIndex,
          ),
        );
      };
      const handleLeave = () => onChange(null);

      svg.addEventListener("mousemove", handleMove);
      svg.addEventListener("mouseleave", handleLeave);
      return () => {
        svg.removeEventListener("mousemove", handleMove);
        svg.removeEventListener("mouseleave", handleLeave);
      };
    }, [scale, left, width, maxIndex, onChange]);

    return <g ref={gRef} />;
  };

  const handleAxisHighlight = (idx) => {
    setHighlightedItemIndex(idx);
  };

  /**
   * Concentric Mark Component:
   * - Inner solid dot: 7x7 (r = 3.5px)
   * - Outer stroke ring: 11x11 (r = 5.5px)
   * - Transparent MarkElement underneath to preserve all native MUI tooltip & hover events
   */
  const ConcentricMark = (props) => {
    const { dataIndex, id, seriesId, x, y } = props;

    // Determine series color
    const isSecond = (id ?? seriesId) === "second";
    const { ringColor, dotColor } = getMarkColors(isSecond, dataIndex);

    return (
      <g style={{ cursor: "default" }}>
        {/* 1. Official MUI MarkElement: Keeps all internal tooltip & interaction handlers active */}
        <MarkElement
          {...props}
          sx={{
            opacity: GlobalConfiguration?.lengthCheckZero,
            cursor: "default",
          }}
        />

        {/* 2. Outer stroke ring (11x11 -> radius 5.5px) */}
        <circle
          cx={x}
          cy={y}
          r={SettingsStatisticsConfiguration?.outerStrokeRingRadius}
          fill="none"
          stroke={ringColor}
          strokeWidth={SettingsStatisticsConfiguration?.outerStrokeWidth}
          style={{ pointerEvents: "none" }}
        />

        {/* 3. Inner solid circle (7x7 -> radius 3.5px) */}
        <circle
          cx={x}
          cy={y}
          r={SettingsStatisticsConfiguration?.innerSolidCircleRadius}
          fill={dotColor}
          stroke="none"
          style={{ pointerEvents: "none" }}
        />
      </g>
    );
  };

  /**
   * Custom LineHighlight Component:
   * Replaces MUI's default green highlight dot with a dynamic concentric highlight ring
   */
  const ConcentricHighlight = (props) => {
    const { x, y, id, seriesId } = props;

    const isSecond = (id ?? seriesId) === "second";
    let markColor = graphDataFirstColorResolved;
    const { dotColor } = getMarkColors(isSecond, highlightedItemIndex);

    if (isSecond) {
      if (enableDynamicZeroColoring) {
        const val =
          formattedGraphDataSecond[highlightedItemIndex] ??
          GlobalConfiguration?.lengthCheckZero;
        const isZero = val === GlobalConfiguration?.lengthCheckZero;
        markColor = isZero ? zeroColorResolved : nonZeroColorResolved;
      } else {
        markColor = graphDataSecondColorResolved;
      }
    }

    return (
      <g style={{ pointerEvents: "none" }}>
        <circle
          cx={x}
          cy={y}
          r={SettingsStatisticsConfiguration?.innerSolidCircleRadius}
          fill={dotColor}
          fillOpacity={1}
        />
        <circle
          cx={x}
          cy={y}
          r={
            SettingsStatisticsConfiguration?.innerSolidCircleRadius +
            SettingsStatisticsConfiguration?.outerStrokeWidth
          }
          fill={markColor}
          fillOpacity={1}
        />
      </g>
    );
  };
  /**
   * MonthLabels - Custom SVG component rendering month range labels below x-axis.
   * Draws vertical ticks, horizontal lines, and abbreviated month names centered
   * over each month's date range. Uses MUI Charts' internal scale and drawing area.
   */
  const MonthLabels = ({ monthGroups }) => {
    const scale = useXScale();
    const { top, height } = useDrawingArea();

    if (
      !scale ||
      !monthGroups ||
      monthGroups.length === GlobalConfiguration?.lengthCheckZero
    )
      return null;

    // Y position for labels: below chart area + configured offset
    const yLine =
      top + height + SettingsStatisticsConfiguration?.graphRangeLabelOffsetY;
    const tickHeight =
      SettingsStatisticsConfiguration?.graphRangeLabelEdgeHeight;

    return (
      <g className="month-labels-group">
        {monthGroups.map((group, idx) => {
          const isSingleDate = group.start === group.end;

          let xStart, xEnd;
          if (isSingleDate) {
            // Single date: center label with half-width on each side
            const xCenter = scale(group.start);
            xStart =
              xCenter -
              SettingsStatisticsConfiguration?.graphRangeLabelSingleDateHalfWidth;
            xEnd =
              xCenter +
              SettingsStatisticsConfiguration?.graphRangeLabelSingleDateHalfWidth;
          } else {
            // Multi-date range: span from first to last date
            xStart = scale(group.start);
            xEnd = scale(group.end);
          }

          // Skip if scale returned invalid values
          if (
            xStart === undefined ||
            xEnd === undefined ||
            isNaN(xStart) ||
            isNaN(xEnd)
          ) {
            return null;
          }

          const xMid = (xStart + xEnd) / GlobalConfiguration?.lengthCheckTwo;
          // Abbreviate month name to first 3 chars (e.g., "Jan 2025" -> "Jan")
          const monthName = group.name
            ? group.name.slice(
                GlobalConfiguration?.lengthCheckZero,
                GlobalConfiguration?.lengthCheckThree,
              )
            : "";
          const gap = SettingsStatisticsConfiguration?.graphMonthLabelGapX;
          return (
            <g key={idx}>
              {/* Left vertical tick */}
              <line
                x1={xStart}
                x2={xStart}
                y1={yLine - tickHeight / GlobalConfiguration?.lengthCheckTwo}
                y2={yLine + tickHeight / GlobalConfiguration?.lengthCheckTwo}
                stroke="var(--primary-nav-act)"
                strokeWidth={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />

              {/* Left horizontal line (only for multi-date ranges) */}
              {!isSingleDate && xStart < xMid - gap && (
                <line
                  x1={xStart}
                  x2={xMid - gap}
                  y1={yLine}
                  y2={yLine}
                  stroke="var(--primary-nav-act)"
                  strokeWidth={
                    SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                  }
                />
              )}

              {/* Month label text */}
              <text
                x={xMid}
                y={yLine}
                fill="var(--text-secondary)"
                fontSize="var(--font-xs)"
                fontWeight="var(--font-normal)"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {monthName}
              </text>

              {/* Right horizontal line (only for multi-date ranges) */}
              {!isSingleDate && xMid + gap < xEnd && (
                <line
                  x1={xMid + gap}
                  x2={xEnd}
                  y1={yLine}
                  y2={yLine}
                  stroke="var(--primary-nav-act)"
                  strokeWidth={
                    SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                  }
                />
              )}

              {/* Right vertical tick */}
              <line
                x1={xEnd}
                x2={xEnd}
                y1={yLine - tickHeight / GlobalConfiguration?.lengthCheckTwo}
                y2={yLine + tickHeight / GlobalConfiguration?.lengthCheckTwo}
                stroke="var(--primary-nav-act)"
                strokeWidth={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />
            </g>
          );
        })}
      </g>
    );
  };

  /**
   * Converts a numeric value to abbreviated string based on given unit.
   * E.g., convertValue(150000, "L") -> "1.5L"
   */
  function convertValue(value, abbreviation) {
    if (value === GlobalConfiguration?.lengthCheckZero)
      return GlobalConfiguration?.dashboardPrimaryCount.slice(-1);

    const scales = {
      "K": 1e3,
      "L": 1e5,
      "Cr": 1e7,
      "M": 1e6,
      "B": 1e9,
      "": 1,
    };

    const divisor = scales[abbreviation] ?? 1;
    const result = value / divisor;

    // Trim trailing zeros, keep 2 decimal places max
    return `${parseFloat(result.toFixed(2))}${abbreviation}`;
  }

  /**
   * Determines the appropriate abbreviation suffix for a given max value.
   * Thresholds follow numbering system (K, Lakh, Crore, M, B).
   */
  function getAbbreviation(maxValue) {
    const thresholds = [
      { limit: 1e3, suffix: "K" }, // >= 1K
      { limit: 1e5, suffix: "L" }, // >= 100K (1 Lakh)
      { limit: 1e6, suffix: "M" }, // >= 10 Lakhs (1 Million)
      { limit: 1e7, suffix: "Cr" }, // >= 100 Lakhs (1 Crore)
      { limit: 1e9, suffix: "B" }, // >= 1000 Crores (1 Billion)
      { limit: 0, suffix: "" }, // default, no abbreviation
    ];
    return thresholds.find((t) => maxValue >= t.limit).suffix;
  }

  return (
    <div className="relative">
      <LineChart
        grid={{ vertical: true }}
        tooltip={{ trigger: "axis" }}
        slots={{ mark: ConcentricMark, lineHighlight: ConcentricHighlight }}
        margin={{
          right: SettingsStatisticsConfiguration.graphPaddingRight, // right padding
          left: SettingsStatisticsConfiguration.graphPaddingLeft, // left padding
        }}
        series={
          graphDataSecond
            ? [
                // First series (primary)
                {
                  id: "first",
                  data: formattedGraphDataFirst,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataFirstColorResolved,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
                // Second series (secondary)
                {
                  id: "second",
                  data: formattedGraphDataSecond,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: secondSeriesActiveColor,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
              ]
            : [
                // Single series mode
                {
                  id: "first",
                  data: formattedGraphDataFirst,
                  area: true,
                  curve: "linear",
                  showMark: true,
                  baseline: "min",
                  color: graphDataFirstColorResolved,
                  valueFormatter: (value) =>
                    amountFormatRequiredForLegend
                      ? `INR ${formatTotalBalance(value, "amount").amount}${
                          amountFormatForLegend
                            ? " " + amountFormatForLegend
                            : ""
                        }`
                      : value,
                },
              ]
        }
        className={`settings-statistics-linechart`}
        xAxis={[
          {
            scaleType: "linear",
            // Use custom indices if provided, otherwise 0..n
            tickInterval:
              defaultDataItemsForXaxis || xAxisArray.map((_, i) => i),
            valueFormatter: (value, context) => {
              const item = xAxisArray[value];
              if (!item) return "";
              const d = dayjs(item);
              // Tooltip header
              if (context?.location === "tooltip") {
                return d.isValid() ? d.format("DD MMM") : item;
              }

              // If valid date, show day number (DD); otherwise show first 3 chars
              return d.isValid()
                ? d.format("DD")
                : item.slice(
                    GlobalConfiguration?.lengthCheckZero,
                    GlobalConfiguration?.lengthCheckThree,
                  );
            },
            data: defaultDataItemsForXaxis,
            tickMinStep: GlobalConfiguration?.lengthCheckOne,
            // Add padding on left/right of chart for first/last points
            min: -(
              (xAxisArray.length - GlobalConfiguration?.lengthCheckOne) *
              SettingsStatisticsConfiguration?.graphXAxisPaddingPercent
            ),
            max:
              (xAxisArray.length - GlobalConfiguration?.lengthCheckOne) *
              (GlobalConfiguration?.lengthCheckOne +
                SettingsStatisticsConfiguration?.graphXAxisPaddingPercent),
            showGrid: true,
            tickLabelStyle: { fontSize: "var(--font-xxs)" },
          },
        ]}
        yAxis={[
          {
            valueFormatter: (value) => {
              // If user selected a specific unit, format with that unit's abbreviation
              if (
                amountFormatForLegend &&
                value !== GlobalConfiguration?.lengthCheckZero
              ) {
                return `${formatTotalBalance(value, "amount")
                  .amount.replace(",", "")
                  .concat("" + abbrMap?.[amountFormatForLegend] || "")}`;
              }
              // Otherwise use auto-detected abbreviation based on data range
              return convertValue(value, yAxisUnit);
            },
            scaleType: "linear",
            tickNumber: SettingsStatisticsConfiguration?.graphYAxisTickCount,
            min: yAxisMin,
            max: yAxisMax,
            tickLabelStyle: { fontSize: "var(--font-xxs)" },
          },
        ]}
        style={{
          height: "var(--statistic-graph-height)",
          width: "var(--width-full)",
        }}
        sx={{
          // ---------------First series styles----------------//
          "& .MuiLineElement-series-first": {
            stroke: graphDataFirstColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-first": {
            fill: graphDataFirstColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },

          // -----------Second series styles----------------//
          "& .MuiLineElement-series-second": {
            stroke: enableDynamicZeroColoring
              ? hasSecondDataNonZero
                ? "url(#secondSeriesLineGradient)"
                : zeroColorResolved
              : graphDataSecondColor,
            strokeDasharray: "4 4", // dotted line
          },
          "& .MuiAreaElement-series-second": {
            fill: enableDynamicZeroColoring
              ? hasSecondDataNonZero
                ? "url(#secondSeriesAreaGradient)"
                : "transparent"
              : graphDataSecondColor,
            fillOpacity:
              SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
          // ------------Common styling---------------------//

          "& .MuiChartsAxisHighlight-root": {
            strokeDasharray:
              SettingsStatisticsConfiguration.zeroStrokeWidthForLineGraph,
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            stroke: "var(--text-primary)",
          },
          // Axis lines
          "& .MuiChartsAxis-root line": {
            stroke: "var(--text-primary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
          },
          // Axis tick marks
          "& .MuiChartsAxis-tick text": {
            fill: "var(--text-primary)",
            fontSize: "var(--font-sm)",
          },
          "& .MuiChartsAxis-tickLabel": {
            fill: "var(--text-primary) !important",
          },

          // Hide horizontal grid lines (cleaner look)
          "& .MuiChartsGrid-horizontalLine": {
            display: "none",
          },

          // Hide Y-axis line and ticks (keep numeric labels only)
          "& .MuiChartsAxis-left .MuiChartsAxis-line": {
            display: "none",
          },
          "& .MuiChartsAxis-left .MuiChartsAxis-tick": {
            display: "none",
          },

          // Style vertical grid lines
          "& .MuiChartsGrid-verticalLine": {
            stroke: "var(--text-secondary)",
            strokeWidth:
              SettingsStatisticsConfiguration.smallStrokeWidthForLineGraph,
            opacity: SettingsStatisticsConfiguration.fillOpacityForLineGraph,
          },
        }}
      >
        <HoverIndexWatcher
          onChange={handleAxisHighlight}
          maxIndex={xAxisArray.length - GlobalConfiguration?.lengthCheckOne}
        />
        {enableDynamicZeroColoring && (
          <defs>
            <linearGradient
              id="secondSeriesLineGradient"
              x1="0"
              y1="1"
              x2="0"
              y2="0"
            >
              <stop
                offset="0%"
                stopColor={zeroColorResolved}
              />
              <stop
                offset="1%"
                stopColor={zeroColorResolved}
              />
              <stop
                offset="2%"
                stopColor={nonZeroColorResolved}
              />
              <stop
                offset="100%"
                stopColor={nonZeroColorResolved}
              />
            </linearGradient>
            <linearGradient
              id="secondSeriesAreaGradient"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor={nonZeroColorResolved}
                stopOpacity={
                  SettingsStatisticsConfiguration?.fillOpacityForLineGraph
                }
              />
              <stop
                offset="100%"
                stopColor={nonZeroColorResolved}
                stopOpacity={
                  SettingsStatisticsConfiguration?.smallStrokeWidthForLineGraph
                }
              />
            </linearGradient>
          </defs>
        )}

        {/* Render month labels only when x-axis contains full dates */}
        {isDateArray(xAxisLabels) &&
          monthGroups?.length > GlobalConfiguration?.lengthCheckZero && (
            <MonthLabels monthGroups={monthGroups} />
          )}
      </LineChart>
    </div>
  );
};

export default CommonLineGraph;
